import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  HostListener,
  inject,
  Input,
  OnInit,
  TemplateRef,
  ViewChild
} from '@angular/core';
import {
  Alert,
  AlertService,
  AlertType,
  EmptyStateModule,
  ModalComponent,
  ModalModule,
  ModalResult,
  PaginatorFetcher,
  PaginatorFilters,
  TableComponent,
  TableConfig,
  TableModule
} from '@michelin/theme';
import { FormArray, FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { map, Observable } from 'rxjs';
import { TranslationService } from '../../../../shared/translations/services/translation.service';
import { ProductDataService } from '../../services/product-data.service';
import { Store } from '@ngxs/store';
import { TranslationState } from '../../../../shared/state/TranslationState';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { FeatureflagsService } from '../../../../shared/services/featureflags.service';
import { FEATURE_FLAGS } from '../../../../shared/constants/constants';
import { ActivatedRoute, Router } from '@angular/router';
import { SharedModule } from '../../../../shared/shared.module';
import { CasingPurchaseDetails } from '../../models/casing-purchase.model';
import { PaginatedCasingPurchaseDetails } from '../../models/response.model';
import { hasRolePermission } from '../../../user-management/utils/UserSetupUtils';
import { ROLES } from '../../../authentication/authRoles.constants';
import { ExportResponse } from '../../../../shared/services/export-to-excel.service';
import { DealerState } from '../../../../shared/state/DealerState';
import { MatDialog } from '@angular/material/dialog';
import { ExportModalComponent, ExportModalData } from '../../../../shared/components/export-modal/export-modal.component';
import { ExportColumnDef } from '../../../../shared/models/export-column-def.model';
import { CanComponentDeactivate } from '../../../../shared/guards/unsaved-changes.guard';

@Component({
  selector: 'app-casing-purchase-setup',
  templateUrl: './casing-purchase-setup.component.html',
  styleUrl: './casing-purchase-setup.component.scss',
  imports: [SharedModule, TableModule, EmptyStateModule, ModalModule],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CasingPurchaseSetupComponent implements OnInit, CanComponentDeactivate {
  @Input() casingPurchaseId: string;
  code: string;
  description: string;
  isCreateMode = false;
  isCopyMode = false;
  @ViewChild(TableComponent, { static: true }) tableRef: TableComponent<CasingPurchaseDetails>;
  @ViewChild('discardConfirmation', { static: true }) discardModal: ModalComponent;
  @ViewChild('discardContent') discardContent: TemplateRef<any>;
  public fb = inject(FormBuilder);
  public store = inject(Store);
  public productDataService = inject(ProductDataService);
  public featureFlagsService = inject(FeatureflagsService);
  public router = inject(Router);
  public alertService = inject(AlertService);
  public translationService = inject(TranslationService);
  public cdr = inject(ChangeDetectorRef);
  public route = inject(ActivatedRoute);
  private dialog = inject(MatDialog);

  initialFormValues: any = {};
  filterForm: FormGroup;
  filterFormBackend: FormGroup;
  casingPurchaseDetails: CasingPurchaseDetails[] = [];
  displayedRowsForm: FormGroup = this.fb.group({ rows: this.fb.array([]) });
  pagination: any;
  pageSizeOptions = [10, 25, 50, 100];
  currentPage = 1;
  pageSize = 10;
  productSaveDisabled = true;
  isCreateInProgress = false;
  saveEnabled = false;

  sizeList: string[] = [];
  descriptionList: string[] = [];
  purchasePriceList: string[] = [];
  // Map to store all modified rows from all pages
  allModifiedRows: Map<string, any> = new Map();

  openExportModal(): void {
    const columns: ExportColumnDef[] = Object.entries(this.columnNames).map(([key, label]) => ({ key, label, selected: true }));
    const data: ExportModalData = {
      columns,
      dataFetcher: this.exportDataFetcher,
      fileNamePrefix: this.dealerName + '-CasingPurchaseSetup-export',
      maxExportRows: 10000
    };
    this.dialog.open(ExportModalComponent, { data, width: '480px' });
  }

  tableConfig: TableConfig<any> = {
    paginator: {
      pageSize: this.pageSize,
      pageSizeOptions: this.pageSizeOptions,
      forceBottomAction: true,
      position: 'bottom'
    },
    columns: {}
  };
  dealerName = this.store.selectSnapshot(DealerState.currentDealer).name;
  columnNames: Record<string, string> = {};

  exportDataFetcher: () => Observable<ExportResponse> = () =>
    this.productDataService.getCasingPurchaseDetails(this.filterFormBackend.value, this.casingPurchaseId, 1, 10000).pipe(
      map((response: any) => ({
        data: (response.data.casingPurchaseDetails as CasingPurchaseDetails[]).map((item) => ({
          size: item.size,
          sizeDescription: item.sizeDescription,
          purchasePrice: item.purchasePrice
        }))
      }))
    );

  fetcher: PaginatorFetcher<any> = (filters: PaginatorFilters) => {
    this.currentPage = filters.page;
    this.pageSize = filters.limit;

    // Store changes from current page before fetching new data
    const modifiedRows = this.getModifiedRows();
    modifiedRows.forEach((row) => {
      this.allModifiedRows.set(row.casingPurchaseDetailId, row);
    });

    return this.productDataService
      .getCasingPurchaseDetails({ ...this.filterFormBackend.value, ...filters }, this.casingPurchaseId, filters.page, filters.limit)
      .pipe(
        map((response: any) => {
          this.casingPurchaseDetails = response.data.casingPurchaseDetails || [];
          this.pagination = response.data.pagination;
          this.updateDisplayedRowsForm(this.casingPurchaseDetails);
          this.displayedRowsForm.markAsPristine();
          this.cdr.markForCheck();
          return { items: this.casingPurchaseDetails, count: this.pagination.totalRecords };
        })
      );
  };

  constructor() {
    this.filterForm = this.fb.group({
      size: [[]],
      description: [[]],
      casingPrice: [[]]
    });
    this.filterFormBackend = this.fb.group({
      size: [[]],
      description: [[]],
      casingPrice: [[]]
    });
  }

  ngOnInit(): void {
    this.casingPurchaseId = this.route.snapshot.paramMap.get('casingPurchasePk');
    const navState = history.state;
    this.code = navState?.code || '';
    this.description = navState?.description || '';
    this.isCopyMode = navState?.isCopy || false;
    this.isCreateMode = !this.casingPurchaseId;

    // If copy mode, clear Code and Description
    if (this.isCopyMode) {
      this.code = '';
      this.description = '';
    }

    this.productSaveDisabled = this.featureFlagsService.getFeatureFlagValue(FEATURE_FLAGS.IS_DUAL_WRITE_ENABLED);
    this.saveEnabled = !this.productSaveDisabled && hasRolePermission(ROLES.edit_product);

    if (this.productSaveDisabled) {
      this.alertService.showAlert({
        type: AlertType.classical,
        title: this.translationService.translate('readOnlyProductDataHeader'),
        content: this.translationService.translate('readOnlyProductDataText'),
        timestamp: 60000
      } as Alert);
    }

    // Load filter dropdowns
    if (this.casingPurchaseId) {
      this.productDataService.getCasingPurchaseDetailsSearchFilters(this.casingPurchaseId).subscribe((response: any) => {
        this.sizeList = response.data.sizes || [];
        this.descriptionList = response.data.descriptions || [];
        this.purchasePriceList = response.data.casingPrice || [];
      });
    }

    this.filterForm.valueChanges.pipe(distinctUntilChanged(), debounceTime(800)).subscribe(() => {
      this.generateFilterForm();
      this.tableRef?.patchExtraFilters(this.filterFormBackend.value, false);
    });

    this.store.select(TranslationState.currentLanguage).subscribe(() => {
      this.columnNames = {
        size: this.translationService.translate('sizeText'),
        sizeDescription: this.translationService.translate('descriptionText'),
        purchasePrice: this.translationService.translate('purchasePriceText')
      };

      this.tableConfig.columns = {
        size: { cell: (row) => row.size, header: this.translationService.translate('sizeText') },
        sizeDescription: { cell: (row) => row.sizeDescription, header: this.translationService.translate('descriptionText') },
        purchasePrice: { cell: 'purchasePrice', header: this.translationService.translate('purchasePriceText') }
      };
      this.cdr.markForCheck();
    });
  }

  /**
   * Gets the rows with modified purchasePrice values
   * @returns Array of modified rows with their new values
   */
  getModifiedRows(): any[] {
    if (!this.initialFormValues || !this.displayedRowsForm) {
      return [];
    }

    const currentValues = this.displayedRowsForm.getRawValue();
    const modifiedRows = [];

    if (currentValues.rows && this.initialFormValues.rows) {
      for (let i = 0; i < currentValues.rows.length; i++) {
        if (i < this.initialFormValues.rows.length) {
          if (currentValues.rows[i].purchasePrice !== this.initialFormValues.rows[i].purchasePrice) {
            modifiedRows.push({
              casingPurchaseDetailId: currentValues.rows[i].casingPurchaseDetailId,
              size: currentValues.rows[i].size,
              sizeDescription: currentValues.rows[i].sizeDescription,
              purchasePrice: currentValues.rows[i].purchasePrice,
              casingSizeFk: currentValues.rows[i].casingSizeFk
            });
          }
        }
      }
    }
    return modifiedRows;
  }

  updateDisplayedRowsForm(rows: any[]): void {
    const rowForms = rows.map((row) => {
      // If this row was modified previously, use the modified value
      const modifiedRow = this.allModifiedRows.get(row.casingPurchaseDetailId);
      return this.fb.group({
        size: [row.size],
        sizeDescription: [row.sizeDescription],
        purchasePrice: [modifiedRow ? modifiedRow.purchasePrice : row.purchasePrice],
        casingPurchaseDetailId: [row.casingPurchaseDetailId],
        casingSizeFk: [row.casingSizeFk]
      });
    });
    const formArray = this.fb.array(rowForms);
    this.displayedRowsForm.setControl('rows', formArray);
    // Store initial form values using baseline values (original API data, not modified)
    const baselineValues = rows.map((row) => ({
      size: row.size,
      sizeDescription: row.sizeDescription,
      purchasePrice: row.purchasePrice, // Use original API value, not modified
      casingPurchaseDetailId: row.casingPurchaseDetailId,
      casingSizeFk: row.casingSizeFk
    }));
    this.initialFormValues = JSON.parse(JSON.stringify({ rows: baselineValues }));
  }

  getControl(item: CasingPurchaseDetails, controlName: string): FormControl {
    const index = this.casingPurchaseDetails.findIndex((row) => row.casingPurchaseDetailId === item.casingPurchaseDetailId);
    if (index === -1) {
      return new FormControl('');
    }
    return (this.displayedRowsForm.get('rows') as FormArray).at(index).get(controlName) as FormControl;
  }

  clearFilters(): void {
    this.filterForm.reset();
  }

  cancel(): void {
    this.router.navigate(['/home/productData/casingPurchase']);
  }

  save(): void {
    if (!this.saveEnabled) {
      return;
    }

    // Store changes from current page first
    const modifiedRows = this.getModifiedRows();
    modifiedRows.forEach((row) => {
      this.allModifiedRows.set(row.casingPurchaseDetailId, row);
    });

    // Convert Map to array for API submission
    const allChanges = Array.from(this.allModifiedRows.values());

    if (allChanges.length === 0) {
      this.alertService.showAlert({
        type: AlertType.strong,
        content: this.translationService.translate('noChangesToSaveText')
      } as Alert);
      return;
    }

    this.isCreateInProgress = true;
    const payload: PaginatedCasingPurchaseDetails = {
      casingPurchaseDetails: allChanges,
      pagination: this.pagination
    };
    this.productDataService.updateCasingPurchaseDetails(payload).subscribe({
      next: () => {
        // Clear the modifications map after successful save
        this.allModifiedRows.clear();
        // Reset the form tracking to prevent unsaved changes popup
        this.initialFormValues = JSON.parse(JSON.stringify(this.displayedRowsForm.getRawValue()));
        this.displayedRowsForm.markAsPristine();

        this.alertService.showAlert({
          type: AlertType.success,
          content: this.translationService.translate('camSaveSuccessText')
        } as Alert);
        this.isCreateInProgress = false;
        this.cdr.markForCheck();
      },
      error: () => {
        this.alertService.showAlert({
          type: AlertType.critical,
          content: this.translationService.translate('criticalErrorAlertText')
        } as Alert);
        this.isCreateInProgress = false;
        this.cdr.markForCheck();
      }
    });
  }

  generateFilterForm(): void {
    this.filterFormBackend.patchValue({
      size: this.filterForm.get('size')?.value || '',
      description: this.filterForm.get('description')?.value || '',
      casingPrice: this.filterForm.get('casingPrice')?.value || ''
    });
  }

  /**
   * Called on keydown to mark deletion intent for environments where inputType is not available.
   */
  handleKeydown(event: KeyboardEvent): void {
    const input = event.target as HTMLInputElement;
    if (!input) {
      return;
    }
    if (event.key === 'Backspace' || event.key === 'Delete') {
      input.dataset.isDeleting = 'true';
    }
  }

  /**
   * Restrict input to at most two digits after decimal point.
   * Allows empty string so user can delete all characters (no forced '0').
   * Preserves caret position and keeps FormControl in sync.
   */
  restrictTwoDecimalPlaces(event: Event, item: CasingPurchaseDetails): void {
    const input = event.target as HTMLInputElement;
    if (!input) {
      return;
    }

    const inputEventType = (event as any)?.inputType ?? '';
    const isDeleting = input.dataset.isDeleting === 'true' || inputEventType.startsWith('delete');

    const originalValue = input.value ?? '';
    const selectionStart = input.selectionStart ?? originalValue.length;

    const cleanValue = (val: string) => {
      if (val === '') {
        return '';
      }
      let v = val.replace(/[^0-9.]/g, '');
      v = v.replace(/(\..*)\./g, '$1');
      if (v === '.') {
        return '0.';
      }
      if (v.startsWith('.')) {
        v = '0' + v;
      }
      if (v.includes('.')) {
        const [intPart, fracPart] = v.split('.');
        v = intPart + '.' + (fracPart ? fracPart.slice(0, 2) : '');
      } else {
        v = v.replace(/^0+(\d)/, '$1');
      }
      return v;
    };

    let newValue = cleanValue(originalValue);

    if (isDeleting && newValue === '0') {
      newValue = '';
    }

    const originalPrefix = originalValue.slice(0, selectionStart);
    let cleanedPrefix = cleanValue(originalPrefix);
    if (isDeleting && cleanedPrefix === '0') {
      cleanedPrefix = '';
    }

    if (input.dataset.isDeleting) {
      delete input.dataset.isDeleting;
    }

    if (newValue === originalValue) {
      try {
        const control = this.getControl(item, 'purchasePrice');
        if (control && control.value !== (newValue === '' ? null : newValue)) {
          control.setValue(newValue === '' ? null : newValue, { emitEvent: false });
        }
      } catch {
        // ignore
      }
      return;
    }

    const newCaretPos = Math.min(cleanedPrefix.length, newValue.length);
    input.value = newValue;
    try {
      input.setSelectionRange(newCaretPos, newCaretPos);
    } catch {
      // ignore
    }

    try {
      const control = this.getControl(item, 'purchasePrice');
      if (control) {
        control.setValue(newValue === '' ? null : newValue, { emitEvent: false });
      }
    } catch {
      // ignore
    }

    this.cdr.markForCheck();
  }

  /**
   * Checks if the form has been modified (purchase price changes)
   */
  isFormChanged(): boolean {
    if (!this.initialFormValues || !this.displayedRowsForm) {
      return false;
    }

    const currentValues = this.displayedRowsForm.getRawValue();

    // Check if rows on current page have been modified
    if (currentValues.rows && this.initialFormValues.rows) {
      for (let i = 0; i < currentValues.rows.length; i++) {
        if (i < this.initialFormValues.rows.length) {
          if (currentValues.rows[i].purchasePrice !== this.initialFormValues.rows[i].purchasePrice) {
            return true;
          }
        }
      }
    }

    // Also check if there are any unsaved modifications in allModifiedRows Map
    if (this.allModifiedRows.size > 0) {
      for (const [key] of this.allModifiedRows.entries()) {
        // Check if this row is on current page
        const currentRow = this.casingPurchaseDetails.find((r) => r.casingPurchaseDetailId === key);
        if (currentRow) {
          // If on current page and matches current form value, it's been reverted - remove it
          const formRow = currentValues.rows?.find((r: { casingPurchaseDetailId: string }) => r.casingPurchaseDetailId === key);
          if (formRow && formRow.purchasePrice === currentRow.purchasePrice) {
            this.allModifiedRows.delete(key);
          } else {
            // Still modified
            return true;
          }
        } else {
          // Not on current page, but has modifications - return true
          return true;
        }
      }
    }

    return false;
  }

  /**
   * Shows the discard changes modal
   */
  showDiscardModal(): void {
    this.discardModal.open({
      id: 'discardConfirmation',
      title: this.translationService.translate('discardChangesText'),
      width: 550,
      content: this.discardContent,
      confirm: this.translationService.translate('okText'),
      cancel: this.translationService.translate('cancelText')
    });
  }

  /**
   * Handles discard modal result
   */
  onDiscardModalClosed(result: ModalResult): void {
    if (result) {
      if (result.id === 'discardConfirmation' && result.result) {
        // User confirmed to discard changes
        this.allModifiedRows.clear();
        this.displayedRowsForm.reset();
        this.cancel();
      }
    }
  }

  /**
   * Prevents unsaved changes from being lost when closing browser tab/window
   */
  @HostListener('window:beforeunload', ['$event'])
  unloadNotification($event: any): void {
    if (this.isFormChanged()) {
      $event.returnValue = true;
    }
  }

  /**
   * Guards the component from deactivation if there are unsaved changes
   */
  canDeactivate(): Promise<boolean> {
    if (this.isFormChanged()) {
      return new Promise<boolean>((resolve) => {
        this.showDiscardModal();
        const modalSubscription = this.discardModal.result.subscribe((result: ModalResult) => {
          if (result && result.id === 'discardConfirmation') {
            resolve(!!result.result);
            modalSubscription.unsubscribe();
          }
        });
      });
    }
    return Promise.resolve(true);
  }
}
